import cn from 'classnames'
import { Button } from 'components/buttons'
import { Input, InputFile, ProductPackaging } from 'components/formParts'
import FormImageList from 'components/formParts/FormImageList'
import Spinner from 'components/Spinner/Spinner'
import { FormikProvider, useFormik } from 'formik'
import { noop } from 'lodash'
import { arrayOf, bool, func, number, object, shape, string } from 'prop-types'
import React, { useState } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import { Col, Row } from 'reactstrap'
import {
  ERRORS,
  ORDER_FILE_INPUT_TYPE,
  ORDER_IMAGE_TYPES,
  ORDER_IMAGES_LIMIT
} from 'utils/constants'
import { getDateAfterBusinessDays, isHoliday, isWeekend } from 'utils/dateTime'
import { fileInputsCheck } from 'utils/orders'
import * as Yup from 'yup'

import st from './OrderProductForm.module.css'

const OrderProductForm = ({
  id,
  className,
  initialValues,
  productId,
  intl,
  isLoading,
  details,
  onImageAdd,
  onImageRemove,
  onImageError,
  imagesLoading,
  onSubmit,
  lastSubmittedValues,
  orderManagementId,
  imagesPending,
  deleteFiles,
  uploadFiles,
  allowCarbCertification,
  certificationFile,
  certificationFilePending
}) => {
  const [isDateTouched, setIsDateTouched] = useState(false)

  const schema = Yup.object().shape({
    shippingDate: Yup.string()
      .required(ERRORS.REQUIRED)
      .test(
        'min-date',
        intl.formatMessage({
          id: 'Shipping date must be at least 2 days from today'
        }),
        function (value) {
          if (!isDateTouched || !value) return true
          const date = new Date(value)
          const minDate = getDateAfterBusinessDays(2)

          return date >= minDate && !isWeekend(date) && !isHoliday(date)
        }
      ),
    parcels: Yup.array().of(
      Yup.object().shape({
        weight: Yup.number()
          .min(0.1, intl.formatMessage({ id: ERRORS.ENTER_A_VALUE }))
          .required(intl.formatMessage({ id: ERRORS.ENTER_A_VALUE })),
        D: Yup.number()
          .min(0.1, intl.formatMessage({ id: ERRORS.ENTER_A_VALUE }))
          .required(intl.formatMessage({ id: ERRORS.ENTER_A_VALUE })),
        W: Yup.number()
          .min(0.1, intl.formatMessage({ id: ERRORS.ENTER_A_VALUE }))
          .required(intl.formatMessage({ id: ERRORS.ENTER_A_VALUE })),
        H: Yup.number()
          .min(0.1, intl.formatMessage({ id: ERRORS.ENTER_A_VALUE }))
          .required(intl.formatMessage({ id: ERRORS.ENTER_A_VALUE }))
      })
    )
  })
  const formik = useFormik({
    initialValues,
    onSubmit: (values) => {
      onSubmit({ ...values, certificationFile }, initialValues)
    },
    validationSchema: schema,
    validateOnChange: true
  })
  const { values, handleChange, handleSubmit, setFieldValue, errors } = formik
  const imageInputs = [
    {
      label: intl.formatMessage({ id: 'Ready item pictures' }),
      type: ORDER_IMAGE_TYPES.READY
    },
    {
      label: intl.formatMessage({ id: 'Packaged item pictures' }),
      type: ORDER_IMAGE_TYPES.PACKED
    }
  ]
  const { handleFileUploadCertification } = uploadFiles
  const { handleFileDeleteCertification } = deleteFiles
  const fileInputs = (values) => {
    return [
      // Show certification field only if the product has MATERIAL: Wood and
      // ships to one of the countries: [US, CA]
      ...(allowCarbCertification
        ? [
            {
              fileUrl: certificationFile,
              pending: certificationFilePending,
              titleClassName: st.fieldTitle,
              containerClassName: st.carbCert,
              type: ORDER_FILE_INPUT_TYPE.CERTIFICATION,
              title: (
                <p className={st.fileInputsTitle}>
                  {intl.formatMessage({ id: 'Certification' })}{' '}
                  {certificationFile ? (
                    <a
                      className={st.fileInputsTitleA}
                      href={certificationFile}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Link
                    </a>
                  ) : null}
                </p>
              ),
              onChange: async (e) => {
                const { files } = e.target
                const allFilesGood = fileInputsCheck(files)

                if (allFilesGood) {
                  const { url, error } = await handleFileUploadCertification({
                    files,
                    productId
                  })

                  if (!error) {
                    onSubmit(
                      { ...values, certificationFile: url },
                      initialValues
                    )
                  }
                }
              },
              onClose: async () => {
                const { url } = await handleFileDeleteCertification({
                  productId
                })
                onSubmit({ ...values, certificationFile: url }, initialValues)
              }
            }
          ]
        : [])
    ].map(({ pending, containerClassName, ...content }, id) => {
      return {
        content: (
          <div className={containerClassName}>
            <InputFile
              disableModify={false}
              isLoading={pending}
              className={st.fileInput}
              {...content}
            />
          </div>
        ),
        id
      }
    })
  }

  const onImageDelete =
    (type) =>
    async ({ id, path }) => {
      const { name, newValues } = await onImageRemove(
        type,
        values,
        orderManagementId,
        productId,
        lastSubmittedValues
      )({ id, path })

      setFieldValue(name, newValues)

      if (name) {
        onSubmit(
          {
            ...{ ...values, [name]: newValues },
            certificationFile
          },
          initialValues
        )
      }
    }

  const onImageChange = (type) => async (e) => {
    const { name, newValues } = await onImageAdd(type, values)(e)

    setFieldValue(name, newValues)
    if (name) {
      onSubmit(
        {
          ...{ ...values, [name]: newValues },
          certificationFile
        },
        initialValues
      )
    }
  }

  const minDate = getDateAfterBusinessDays(2)
  const minDateString = minDate.toISOString().split('T')[0]

  return (
    <FormikProvider value={formik} className={cn(st.form, className)}>
      <Row className={st.orderShippingDetailsRow}>
        {details()}
        <Col className={st.colRight} lg={6} xs={12}>
          <Row className="rowWide">
            <Col className={st.marginBottom} md={5} xs={12}>
              <Input
                label={`${intl.formatMessage({
                  id: 'Artisan confirmed shipping date'
                })}:`}
                labelClassName={st.fieldTitle}
                className={st.field}
                name="shippingDate"
                type="date"
                min={minDateString}
                onChange={(e) => {
                  setIsDateTouched(true)
                  handleChange(e)
                }}
                onBlur={(e) => {
                  setIsDateTouched(true)
                  formik.handleBlur(e)
                }}
                value={values.shippingDate}
                error={errors.shippingDate}
              />
              <Input
                label={`${intl.formatMessage({ id: 'Comments' })}:`}
                labelClassName={st.fieldTitle}
                name="comments"
                type="textarea"
                onChange={handleChange}
                value={values.comments}
              />
            </Col>
            <Col className={st.marginBottom} md={7} xs={12}>
              <Row className={cn('rowWide', st.marginBottom)}>
                {fileInputs(values, setFieldValue).map((fi) => {
                  return <div key={fi.id}>{fi.content}</div>
                })}
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className={st.imagesRow}>
        {imageInputs.map((imageInput) => {
          const disableModify =
            values[`images${imageInput.type}`].length >= ORDER_IMAGES_LIMIT
          return (
            <FormImageList
              key={imageInput.type}
              onChange={onImageChange(imageInput.type)}
              imageInput={imageInput}
              onImageError={onImageError}
              imagesLoading={imagesLoading}
              disableModify={disableModify}
              onRemove={onImageDelete(imageInput.type)}
              imageListItems={values[`images${imageInput.type}`]}
              imagesPending={imagesPending}
            />
          )
        })}
      </Row>
      <div className={st.packages}>
        <ProductPackaging
          productId={id}
          parcels={values.parcels}
          lightInputs
          noCost
        />
      </div>
      <Row>
        <Col>
          <div className={st.buttons}>
            <Button
              disabled={isLoading}
              black
              type="submit"
              onClick={handleSubmit}
            >
              {isLoading ? (
                <Spinner
                  color="--main-bg-light"
                  width={43}
                  length={3}
                  size={14}
                  show
                />
              ) : (
                <FormattedMessage id="Save" />
              )}
            </Button>
          </div>
        </Col>
      </Row>
    </FormikProvider>
  )
}

OrderProductForm.defaultProps = {
  className: '',
  initialValues: {
    comments: '',
    shippingDate: '',
    parcels: []
  },
  inputFileCntClassName: '',
  onFileChange: noop,
  onFileClose: noop,
  onSubmit: noop,
  isLoading: false,
  details: noop,
  onImageAdd: noop,
  onImageError: noop,
  imagesLoading: [],
  onImageRemove: noop,
  lastSubmittedValues: {},
  orderManagementId: '',
  imagesPending: {},
  productId: '',
  canApprove: false
}
OrderProductForm.propTypes = {
  id: string,
  className: string,
  initialValues: shape({
    comments: string,
    productId: string.isRequired,
    shippingDate: string,
    parcels: arrayOf(
      shape({
        amountCurrency: string,
        deep: number,
        height: number,
        name: string,
        shipmentDataLoaded: bool,
        weight: number,
        width: number
      })
    )
  }),
  inputFileCntClassName: string,
  onFileChange: func,
  onFileClose: func,
  onSubmit: func,
  isLoading: bool,
  intl: object,
  details: func,
  onImageAdd: func,
  onImageError: func,
  onImageRemove: func,
  imagesLoading: arrayOf(string),
  lastSubmittedValues: shape({}),
  orderManagementId: string,
  imagesPending: shape({}),
  productId: string,
  deleteFiles: shape({}),
  uploadFiles: shape({}),
  canApprove: bool,
  allowCarbCertification: bool,
  certificationFile: string,
  certificationFilePending: bool
}

export default injectIntl(OrderProductForm)
